import React, { useCallback, useState } from 'react';
import { BarChart3, Play, RefreshCw, FlaskConical, Layers, ListChecks, Loader2, Package, PhoneCall, Radio, ShoppingBag, Users } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import CoverageNote from './components/CoverageNote';
import DownloadMenu from './components/DownloadMenu';
import DrilldownDrawer from './components/DrilldownDrawer';
import FilterBar from './components/FilterBar';
import { ReportProvider, useReport } from './ReportContext';
import AgentBucketTab from './tabs/AgentBucketTab';
import CallsTab from './tabs/CallsTab';
import CategoryTab from './tabs/CategoryTab';
import OrdersRevenueTab from './tabs/OrdersRevenueTab';
import OverviewTab from './tabs/OverviewTab';
import WorkTab from './tabs/WorkTab';
import type { DrillContext, Filters } from './types';

type TabKey = 'overview' | 'work' | 'orders' | 'agents' | 'calls' | 'category';

const TABS: { key: TabKey; label: string; question: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', question: 'How is the business doing?', icon: <BarChart3 className="h-4 w-4" /> },
  { key: 'work', label: 'Work', question: 'How much work came in, and how much was worked?', icon: <ListChecks className="h-4 w-4" /> },
  { key: 'orders', label: 'Orders & Revenue', question: 'Which orders came, what did they earn, and who gets credit?', icon: <ShoppingBag className="h-4 w-4" /> },
  { key: 'agents', label: 'Agent × Bucket', question: 'Who did what, in which workstream?', icon: <Users className="h-4 w-4" /> },
  { key: 'calls', label: 'Calls & Connectivity', question: 'Are we reaching customers, and how much time does it take?', icon: <PhoneCall className="h-4 w-4" /> },
  { key: 'category', label: 'Category › Product › Agent', question: 'What sold, in which category, and who sold it?', icon: <Package className="h-4 w-4" /> },
];

const iso = (d: Date) => d.toISOString().slice(0, 10);
const DEFAULTS: Filters = {
  // Last 7 days: a short default keeps the first load light on the database. Widen it when needed.
  from: iso(new Date(Date.now() - 6 * 86400000)),
  to: iso(new Date()),
  fromTime: '', toTime: '',
  grouping: 'daily', agent: 'all', buckets: [], worked: 'all', disposition: 'all',
  orderStatus: 'all', attribution: 'all', coupon: '', category: 'all', product: 'all', search: '',
};

/**
 * Performance Report (Beta) -- layout prototype. Every number comes from mockData.ts;
 * nothing here calls the API. Opened by URL only, it has no menu entry.
 */
const Body: React.FC<{ filters: Filters; setFilters: React.Dispatch<React.SetStateAction<Filters>>; sample: boolean }> = ({ filters, setFilters, sample }) => {
  const [tab, setTab] = useState<TabKey>('overview');
  const [drill, setDrill] = useState<DrillContext | null>(null);
  const { rows, calls, loading, error, asOf, refreshLive, run, hasRun, stale } = useReport();
  const { userData } = useUser();
  const isSuperAdmin = (userData as { data?: { role?: { role_id?: number } } } | null)?.data?.role?.role_id === 9401;
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  // One live refresh per login: the flag remembers the session token, so a new login shows the button again.
  const session = (() => { try { return document.cookie.split(';').find(c => c.trim().startsWith('crm_auth_token='))?.split('=')[1] ?? 'session'; } catch { return 'session'; } })();
  const [refreshUsed, setRefreshUsed] = useState(() => { try { return localStorage.getItem('perf-report-refresh-used') === session; } catch { return false; } });
  const runRefresh = async () => {
    setConfirmRefresh(false);
    try { localStorage.setItem('perf-report-refresh-used', session); } catch { /* the button just stays hidden for this visit */ }
    setRefreshUsed(true);
    await refreshLive();
  };
  const patch = useCallback((p: Partial<Filters>) => setFilters(f => ({ ...f, ...p })), []);
  const closeDrill = useCallback(() => setDrill(null), []);
  const active = TABS.find(t => t.key === tab) ?? TABS[0];

  return (
    <div className="min-h-full w-full bg-white p-4 dark:bg-slate-900 md:p-6">
      <div className="w-full space-y-5">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 lg:flex-1">
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-800 dark:text-white">
              <Layers className="h-6 w-6 text-violet-400" /> Performance Report
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-400/20 dark:text-violet-200">Beta</span>
            </h1>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
              Work, orders and credit kept separate: how much came in, how much was worked, what it earned, and who gets credit.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                  {sample ? (
                    <span className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-0.5 text-xs font-medium text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
                      <FlaskConical className="h-3.5 w-3.5" /> Sample data — layout preview only
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-0.5 text-xs font-medium text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-200">
                      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radio className="h-3.5 w-3.5" />}
                      Live data{asOf && !loading ? ` · as of ${new Date(asOf).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </span>
                  )}
                  {isSuperAdmin && !sample && hasRun && !refreshUsed && (
                    <button type="button" onClick={() => setConfirmRefresh(true)}
                      className="flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-3 py-0.5 text-xs font-medium text-violet-800 hover:bg-violet-100">
                      <RefreshCw className="h-3.5 w-3.5" /> Refresh with live data
                    </button>
                  )}
                  <CoverageNote work={tab !== 'orders' && tab !== 'category'} orders={tab !== 'work' && tab !== 'agents'} />
            </div>
          </div>
          <div className="shrink-0 lg:pt-0.5">
            <FilterBar filters={filters} onChange={patch} onReset={() => setFilters(DEFAULTS)}
              actions={<>
                <button type="button" onClick={run} disabled={loading}
                  className="flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-wait disabled:opacity-70">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {loading ? 'Running…' : hasRun ? 'Run again' : 'Run report'}
                </button>
                {hasRun && <DownloadMenu tab={tab} tabLabel={active.label} rows={rows} calls={calls} filters={filters} />}
              </>} />
          </div>
        </header>
        <nav className="flex flex-wrap gap-x-1 border-b border-slate-200 dark:border-slate-700" role="tablist" aria-label="Report sections">
          {TABS.map(t => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${tab === t.key
                ? 'border-violet-500 text-violet-800 dark:text-violet-200'
                : 'border-transparent text-slate-700 hover:text-slate-800 dark:text-slate-200 dark:hover:text-white'}`}>
              {t.icon}{t.label}
            </button>
          ))}
        </nav>
        {tab !== 'agents' && <p className="text-sm text-slate-700 dark:text-slate-300">{active.question}</p>}
        {!hasRun && (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 px-6 py-16 text-center dark:border-slate-600">
            <Play className="h-8 w-8 text-violet-400" />
            <p className="text-sm text-slate-700 dark:text-slate-300">Choose the date, time and filters above, then press <b>Run report</b>. Nothing loads until you do.</p>
          </div>
        )}
        {hasRun && stale && !loading && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">Filters changed. The numbers below are from the last run. Press <b>Run again</b> to update them.</p>
        )}
        {error && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}. Numbers below may be out of date.</p>
        )}

        {hasRun && tab === 'overview' && <OverviewTab rows={rows} openDrill={setDrill} />}
        {hasRun && tab === 'work' && <WorkTab rows={rows} openDrill={setDrill} />}
        {hasRun && tab === 'orders' && <OrdersRevenueTab rows={rows} openDrill={setDrill} />}
        {hasRun && tab === 'agents' && <AgentBucketTab rows={rows} openDrill={setDrill} />}
        {hasRun && tab === 'calls' && <CallsTab calls={calls} rows={rows} openDrill={setDrill} />}
        {hasRun && tab === 'category' && <CategoryTab rows={rows} filters={filters} openDrill={setDrill} />}
      </div>
      {confirmRefresh && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label="Refresh with live data">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Refresh with live data?</h3>
            <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
              The report normally shows numbers prepared about 30 minutes ago. This rebuilds the current view from the database now, which takes
              about 10–30 seconds and puts extra load on the system agents are using. You can do this once per login.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmRefresh(false)}
                className="rounded-lg border border-slate-300 px-4 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200">Cancel</button>
              <button type="button" onClick={runRefresh}
                className="rounded-lg bg-violet-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-violet-700">Yes, refresh now</button>
            </div>
          </div>
        </div>
      )}
      <DrilldownDrawer ctx={drill} onClose={closeDrill} />
    </div>
  );
};

const PerformanceReport: React.FC = () => {
  const [filters, setFilters] = useState<Filters>(DEFAULTS);
  // ?sample=1 opens the layout with made-up numbers; live data is the default.
  const [sample] = useState(() => new URLSearchParams(window.location.search).get('sample') === '1');
  return (
    <ReportProvider filters={filters} sample={sample}>
      <Body filters={filters} setFilters={setFilters} sample={sample} />
    </ReportProvider>
  );
};

export default PerformanceReport;
